/**
 * 로그인 패널 배경 — 번진 빛 몇 겹 위에 깊이가 다른 별 세 층.
 * 고정 시드 난수로 만들어 매번 같은 모양이지만 손으로 찍은 듯 고르지 않다. 모듈 로드 때 한 번만 계산한다.
 */
const W = 600
const H = 900

function rng(seed: number) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// depth 0: 먼 별(흐림) · 1: 중간 · 2: 가까운 별(또렷)
type Star = { x: number; y: number; r: number; o: number; depth: 0 | 1 | 2; lit?: boolean }

function build() {
  const rand = rng(20261002)
  const stars: Star[] = []

  const layer = (count: number, depth: Star['depth'], r: [number, number], o: [number, number]) => {
    for (let k = 0; k < count; k++) {
      stars.push({
        x: rand() * W, y: rand() * H, depth,
        r: r[0] + rand() * (r[1] - r[0]),
        o: o[0] + rand() * (o[1] - o[0]),
      })
    }
  }
  layer(110, 0, [0.6, 1.6], [0.12, 0.3])
  layer(45, 1, [1, 2.4], [0.25, 0.5])
  layer(16, 2, [1.6, 3.2], [0.4, 0.65])

  // 가까운 층의 큰 별 몇 개만 따뜻한 흰빛으로 조금 더 밝게 둔다. 로고·저작권 근처(위아래 흐린 구간)는 켜지 않는다
  const near = stars
    .map((s, i) => [s, i] as const)
    .filter(([s]) => s.depth === 2 && s.y > H * 0.24 && s.y < H * 0.8 && s.x > 60 && s.x < W - 60)
    .sort((a, b) => b[0].r - a[0].r)
  near.slice(0, 5).forEach(([, i]) => { stars[i].lit = true })

  return stars
}

const STARS = build()

// 위(로고)·아래(저작권) 쪽은 흐리게
const FADE = 'linear-gradient(to bottom, transparent 0%, black 24%, black 80%, transparent 100%)'

export default function Constellation({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden className={`overflow-hidden ${className}`}>
      {/* 번진 빛 — 서로 다른 위치·크기로 겹쳐 층을 만든다 */}
      <div className="absolute -left-[20%] top-[18%] w-[80%] aspect-square rounded-full bg-[oklch(0.5_0.08_280)]/16 blur-[100px]" />
      <div className="absolute -right-[25%] top-[42%] w-[85%] aspect-square rounded-full bg-[oklch(0.55_0.13_258)]/25 blur-[110px]" />
      <div className="absolute left-[15%] -bottom-[15%] w-[70%] aspect-square rounded-full bg-[oklch(0.6_0.1_190)]/15 blur-[100px]" />
      {/* 위아래로 어둡게 눌러 빛이 가운데에 머물게 */}
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,var(--color-bar)_0%,transparent_30%,transparent_70%,var(--color-bar)_100%)]" />

      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full"
        style={{ maskImage: FADE, WebkitMaskImage: FADE }}>
        <defs>
          <filter id="cst-far" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="1.3" /></filter>
          <filter id="cst-mid" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="0.5" /></filter>
          <filter id="cst-glow" x="-200%" y="-200%" width="500%" height="500%"><feGaussianBlur stdDeviation="5" /></filter>
        </defs>

        <g filter="url(#cst-far)" fill="white">
          {STARS.filter(s => s.depth === 0).map((s, i) => <circle key={i} cx={s.x} cy={s.y} r={s.r * 1.4} fillOpacity={s.o} />)}
        </g>
        <g filter="url(#cst-mid)" fill="white">
          {STARS.filter(s => s.depth === 1).map((s, i) => <circle key={i} cx={s.x} cy={s.y} r={s.r} fillOpacity={s.o} />)}
        </g>
        {STARS.filter(s => s.depth === 2).map((s, i) => s.lit ? (
          <g key={i} fill="oklch(0.97 0.035 90)">
            <circle cx={s.x} cy={s.y} r={s.r * 3.5} opacity={0.14} filter="url(#cst-glow)" />
            <circle cx={s.x} cy={s.y} r={s.r * 1.6} opacity={0.15} filter="url(#cst-mid)" />
            <circle cx={s.x} cy={s.y} r={s.r * 0.8} opacity={0.8} filter="url(#cst-mid)" />
          </g>
        ) : (
          <circle key={i} cx={s.x} cy={s.y} r={s.r * 0.8} fill="white" fillOpacity={s.o} />
        ))}
      </svg>
    </div>
  )
}
